import type {
  IActiveProbe,
  IBulkDeleteProbePayload,
  IBulkProbeOperationResult,
  IBulkToggleProbePayload,
  ICreateProbeDto,
  IDownloadSourceFileParams,
  IGeneratedProbeData,
  IGenerateProbeFromFileParams,
  IPresignProbeFileUploadParams,
  IPresignProbeFileUploadResponse,
  IProbe,
  IUpdateProbeDto,
} from "../types";
import type { ApiClient } from "./client";

export class ProbesApi {
  constructor(private readonly apiClient: ApiClient) {}

  async getProbes(): Promise<IProbe[]> {
    const response = await this.apiClient.get<IProbe[]>("/probes");
    return response.data;
  }

  async getActiveProbes(): Promise<IActiveProbe[]> {
    const response = await this.apiClient.get<IActiveProbe[]>("/probes/active");
    return response.data;
  }

  async getProbeByUuid(uuid: string): Promise<IProbe> {
    const response = await this.apiClient.get<IProbe>(`/probes/${uuid}`);
    return response.data;
  }

  async createProbe(data: ICreateProbeDto): Promise<IProbe> {
    const response = await this.apiClient.post<IProbe>("/probes", data);
    return response.data;
  }

  async updateProbe(uuid: string, data: IUpdateProbeDto): Promise<IProbe> {
    const response = await this.apiClient.patch<IProbe>(
      `/probes/${uuid}`,
      data,
    );
    return response.data;
  }

  async toggleStatus(uuid: string, isEnabled: boolean): Promise<IProbe> {
    const response = await this.apiClient.patch<IProbe>(
      `/probes/${uuid}/status`,
      { isEnabled },
    );
    return response.data;
  }

  async deleteProbe(uuid: string): Promise<void> {
    await this.apiClient.delete(`/probes/${uuid}`);
  }

  async bulkToggleProbes(
    payload: IBulkToggleProbePayload,
  ): Promise<IBulkProbeOperationResult> {
    const response = await this.apiClient.put<IBulkProbeOperationResult>(
      "/probes/bulk-toggle",
      payload,
    );
    return response.data;
  }

  async bulkDeleteProbes(
    payload: IBulkDeleteProbePayload,
  ): Promise<IBulkProbeOperationResult> {
    const response = await this.apiClient.delete<IBulkProbeOperationResult>(
      "/probes/bulk-delete",
      { data: payload },
    );
    return response.data;
  }

  async presignFileUpload(
    params: IPresignProbeFileUploadParams,
  ): Promise<IPresignProbeFileUploadResponse> {
    const response = await this.apiClient.post<IPresignProbeFileUploadResponse>(
      "/probes/files/presign",
      params,
    );
    return response.data;
  }

  async generateProbeFromFile(
    params: IGenerateProbeFromFileParams,
  ): Promise<IGeneratedProbeData> {
    const { signal, ...body } = params;
    const response = await this.apiClient.post<IGeneratedProbeData>(
      "/probes/generate-from-file",
      body,
      ...(signal === undefined ? [] : [{ signal }]),
    );
    return response.data;
  }

  // NOTE: Delete orphaned S3 file when user cancels upload
  async cleanupUploadedFile(params: { s3Key: string }): Promise<void> {
    await this.apiClient.post("/probes/files/cleanup", params);
  }

  async downloadSourceFile(params: IDownloadSourceFileParams): Promise<Blob> {
    const { probeUuid, onProgress } = params;

    const response = await this.apiClient.get<Blob>(
      `/probes/${probeUuid}/file/download`,
      {
        responseType: "blob",
        onDownloadProgress: onProgress
          ? (event) => onProgress({ loaded: event.loaded, total: event.total })
          : undefined,
      },
    );
    return response.data;
  }
}
